import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Calendar, Clock, ArrowRight, Search } from 'lucide-react';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
    { slug: 'all', name: 'All Posts' },
    { slug: 'seo', name: 'SEO' },
    { slug: 'gmb-local-seo', name: 'GMB / Local SEO' },
    { slug: 'ai-search', name: 'AI Search' },
    { slug: 'case-studies', name: 'Case Studies' },
    { slug: 'guides', name: 'Guides' },
];

// Sample blog posts for demo
const samplePosts = [
    {
        id: '1',
        title: 'The Complete Guide to GMB Optimization in 2024',
        slug: 'gmb-optimization-guide-2024',
        excerpt: 'Learn how to optimize your Google Business Profile to rank #1 in local searches and drive more foot traffic.',
        category: 'gmb-local-seo',
        author: 'Vikram Singh',
        featured_image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop',
        created_at: '2024-01-15T10:00:00Z',
        tags: ['GMB', 'Local SEO', 'Google Maps'],
    },
    {
        id: '2',
        title: 'How to Rank in ChatGPT and AI Search Engines',
        slug: 'rank-in-chatgpt-ai-search',
        excerpt: 'AI search is changing how people find information. Here\'s how to optimize your content for ChatGPT, Perplexity, and Google SGE.',
        category: 'ai-search',
        author: 'Priya Patel',
        featured_image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&auto=format&fit=crop',
        created_at: '2024-01-10T10:00:00Z',
        tags: ['AI Search', 'ChatGPT', 'SGE'],
    },
    {
        id: '3',
        title: 'Programmatic SEO: Scaling to 1000+ Pages',
        slug: 'programmatic-seo-scaling-guide',
        excerpt: 'A step-by-step guide to building and scaling programmatic SEO pages that rank and convert.',
        category: 'seo',
        author: 'Arjun Reddy',
        featured_image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&auto=format&fit=crop',
        created_at: '2024-01-05T10:00:00Z',
        tags: ['Programmatic SEO', 'Scaling', 'Automation'],
    },
    {
        id: '4',
        title: 'Case Study: How We 5x\'d a Clinic\'s Leads in 3 Months',
        slug: 'clinic-case-study-5x-leads',
        excerpt: 'Deep dive into our strategy for a multi-location healthcare clinic that resulted in 5x lead growth.',
        category: 'case-studies',
        author: 'Sneha Kapoor',
        featured_image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&auto=format&fit=crop',
        created_at: '2024-01-01T10:00:00Z',
        tags: ['Case Study', 'Healthcare', 'Local SEO'],
    },
    {
        id: '5',
        title: 'Local SEO Checklist: 50 Points for Rankings',
        slug: 'local-seo-checklist-50-points',
        excerpt: 'The ultimate checklist for local SEO success. Cover all 50 points to dominate local search.',
        category: 'guides',
        author: 'Vikram Singh',
        featured_image: 'https://images.unsplash.com/photo-1553484771-8bbd3e39ce35?w=800&auto=format&fit=crop',
        created_at: '2023-12-28T10:00:00Z',
        tags: ['Local SEO', 'Checklist', 'Guide'],
    },
    {
        id: '6',
        title: 'High-Intent Keywords: Finding the Money Keywords',
        slug: 'high-intent-keywords-guide',
        excerpt: 'Not all traffic is equal. Learn how to find and target keywords that actually drive revenue.',
        category: 'seo',
        author: 'Priya Patel',
        featured_image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop',
        created_at: '2023-12-20T10:00:00Z',
        tags: ['Keywords', 'SEO Strategy', 'Conversions'],
    },
];

export default function BlogPage() {
    const [posts, setPosts] = useState(samplePosts);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchPosts();
    }, []);

    const fetchPosts = async () => {
        try {
            const response = await axios.get(`${API}/blog`);
            if (response.data.length > 0) {
                setPosts(response.data);
            }
        } catch (error) {
            console.log('Using sample posts');
        } finally {
            setLoading(false);
        }
    };

    const filteredPosts = posts.filter(post => {
        const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
        const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    return (
        <div className="relative" data-testid="blog-page">
            {/* Hero */}
            <section className="section-padding bg-muted/30">
                <div className="container-custom">
                    <div className="max-w-4xl">
                        <p className="text-sm font-medium tracking-wider uppercase text-primary mb-4">
                            Blog
                        </p>
                        <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
                            Insights & Strategies
                        </h1>
                        <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                            Expert insights on SEO, local search, AI optimization, and digital growth 
                            strategies to help your business succeed.
                        </p>
                    </div>
                </div>
            </section>

            {/* Filters */}
            <section className="py-8 border-b border-border sticky top-16 md:top-20 bg-background/80 backdrop-blur-xl z-40">
                <div className="container-custom">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        {/* Categories */}
                        <div className="flex flex-wrap gap-2">
                            {categories.map((category) => (
                                <button
                                    key={category.slug}
                                    onClick={() => setSelectedCategory(category.slug)}
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                        selectedCategory === category.slug
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                                    }`}
                                    data-testid={`category-${category.slug}`}
                                >
                                    {category.name}
                                </button>
                            ))}
                        </div>

                        {/* Search */}
                        <div className="relative w-full md:w-72">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                type="text"
                                placeholder="Search posts..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10"
                                data-testid="blog-search"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Posts Grid */}
            <section className="section-padding">
                <div className="container-custom">
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                                <div key={i} className="rounded-2xl border border-border overflow-hidden">
                                    <div className="aspect-video skeleton" />
                                    <div className="p-6 space-y-4">
                                        <div className="h-6 skeleton rounded w-3/4" />
                                        <div className="h-4 skeleton rounded w-full" />
                                        <div className="h-4 skeleton rounded w-2/3" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : filteredPosts.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {filteredPosts.map((post, index) => (
                                <Link
                                    key={post.id}
                                    to={`/blog/${post.slug}`}
                                    className="group rounded-2xl border border-border overflow-hidden bg-background hover:border-primary/30 transition-colors card-hover"
                                    data-testid={`blog-post-${index}`}
                                >
                                    <div className="aspect-video overflow-hidden">
                                        <img 
                                            src={post.featured_image || 'https://images.unsplash.com/photo-1432821596592-e2c18b78144f?w=800&auto=format&fit=crop'}
                                            alt={post.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                                            <span className="px-2 py-1 rounded bg-primary/10 text-primary text-xs font-medium">
                                                {categories.find(c => c.slug === post.category)?.name || post.category}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                {formatDate(post.created_at)}
                                            </span>
                                        </div>
                                        <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                                            {post.title}
                                        </h3>
                                        <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                                            {post.excerpt}
                                        </p>
                                        <div className="flex items-center gap-2 text-primary font-medium text-sm">
                                            Read More
                                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20">
                            <p className="text-lg text-muted-foreground mb-4">No posts found</p>
                            <Button 
                                variant="outline" 
                                onClick={() => { setSelectedCategory('all'); setSearchQuery(''); }}
                                className="rounded-full"
                            >
                                Clear Filters
                            </Button>
                        </div>
                    )}
                </div>
            </section>

            {/* Newsletter CTA */}
            <section className="section-padding bg-muted/30">
                <div className="container-custom">
                    <div className="max-w-2xl mx-auto text-center">
                        <h2 className="text-3xl font-semibold tracking-tight mb-4">
                            Stay Updated
                        </h2>
                        <p className="text-muted-foreground mb-8">
                            Get the latest SEO insights and strategies delivered to your inbox.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                            <Input 
                                type="email" 
                                placeholder="Enter your email"
                                className="flex-1"
                            />
                            <Button className="btn-glow rounded-full px-6">
                                Subscribe
                            </Button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
